import { useState, useEffect } from "react";
import { BarChart3, Star } from "lucide-react";
import { getApiUrl } from "../api/config";


// ── Question label mapping ──
const ASSESSMENT_QUESTIONS = {
  overallExperienceRating: "How would you rate the overall experience of taking the BotGuru AI Prep-Interview assessment?",
  questionQualityRating: "How would you rate the relevance and quality of the questions asked during the assessment?",
  improvementUsefulnessRating: "How would you rate the areas of improvement suggested by BotGuru for preparing for future interviews?",
  improvementActionLikelihoodRating: "How likely are you to work on the improvement areas suggested by BotGuru to strengthen your skills?",
  retakeLikelihoodRating: "How likely are you to take the BotGuru Prep-Interview again after improving your skills?",
  recommendationLikelihoodRating: "How likely are you to recommend BotGuru AI Prep-Interview to other students for interview preparation?",
};

const REPORT_QUESTIONS = {
  technicalEvaluationRating: "How well do you think BotGuru evaluated your technical knowledge and problem-solving ability?",
  reportFeedbackAgreementRating: "How much do you agree with the evaluation report and feedback provided by BotGuru on your performance?",
};

const mapApiToQuestions = (apiData, questionMap) =>
  Object.entries(questionMap).map(([key, text], i) => {
    const d = apiData?.[key];
    return {
      id: i + 1,
      text,
      avgRating: Math.round((parseFloat(d?.averageRating) || 0) * 10) / 10,
      totalResponses: d?.totalAnswered ?? 0,
      distribution: {
        5: d?.stars?.["5"]?.percentage ?? 0,
        4: d?.stars?.["4"]?.percentage ?? 0,
        3: d?.stars?.["3"]?.percentage ?? 0,
        2: d?.stars?.["2"]?.percentage ?? 0,
        1: d?.stars?.["1"]?.percentage ?? 0,
      },
    };
  });

const StarDisplay = ({ rating, size = 25 }) => {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => {
        const fill = Math.min(Math.max(rating - (i - 1), 0), 1);
        const id = `star-fill-${i}-${Math.round(fill * 100)}`;
        return (
          <svg key={i} width={size} height={size} viewBox="0 0 24 24" style={{ opacity: fill > 0 ? 1 : 0.3 }}>
            <defs>
              <linearGradient id={id}>
                <stop offset={`${fill * 100}%`} stopColor="#f59e0b" />
                <stop offset={`${fill * 100}%`} stopColor="transparent" />
              </linearGradient>
            </defs>
            <path
              d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
              fill={`url(#${id})`}
              stroke="#f59e0b"
              strokeWidth={1.5}
              strokeLinejoin="round"
            />
          </svg>
        );
      })}
    </div>
  );
};

const QuestionCard = ({ question, index, barColor }) => {
const maxPct = 100;
  return (
    <div className="bg-card-bg border border-purple-glow-10 rounded-xl overflow-hidden relative hover:border-purple-glow-30 hover:shadow-glow-sm transition-all">
      <div className="absolute top-0 left-0 right-0 h-[3px]" style={{ background: barColor }} />
      <div className="p-5">
        <div className="flex items-start gap-3 mb-4">
          <span className="w-7 h-7 rounded-full text-white text-xs font-bold flex items-center justify-center shrink-0 mt-0.5" style={{ background: barColor }}>{index + 1}</span>
          <p className="text-md font-bold text-text-main leading-relaxed m-0">{question.text}</p>
        </div>
        <div className="flex items-center gap-8">
          <div className="flex flex-col items-center gap-1 min-w-[100px] shrink-0">
            <span className="text-4xl font-extrabold text-text-main leading-none">{question.avgRating.toFixed(1)}</span>
            <StarDisplay rating={question.avgRating} />
            <span className="text-xs text-text-sub mt-1">{question.totalResponses} total responses</span>
          </div>
          <div className="flex-1 flex flex-col gap-1.5">
            {[5, 4, 3, 2, 1].map((star) => {
              const pct = question.distribution[star];
              return (
                <div key={star} className="flex items-center gap-2">
                  <span className="flex items-center gap-0.5 text-sm font-semibold text-text-sub min-w-[28px] justify-end">
                    {star} <Star size={16} fill="#f59e0b" stroke="#f59e0b" />
                  </span>
                  <div className="flex-1 h-3.5 bg-purple-glow-5 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: barColor, minWidth: pct > 0 ? "4px" : "0px" }} />
                  </div>
                  <span className="text-sm font-semibold text-text-sub min-w-[30px] text-right">{pct}%</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

const SectionHeader = ({ title, subtitle, gradient }) => (
  <div className="flex items-center gap-4 px-6 py-5 rounded-xl text-white" style={{ background: gradient, boxShadow: "0 4px 20px rgba(0,0,0,0.15)" }}>
    <div className="w-11 h-11 rounded-lg bg-white/20 flex items-center justify-center shrink-0">
      <BarChart3 size={22} />
    </div>
    <div>
      <h2 className="text-lg font-bold m-0">{title}</h2>
      <p className="text-sm m-0 mt-0.5 opacity-85">{subtitle}</p>
    </div>
  </div>
);

function StudentFeedback() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [assessmentQuestions, setAssessmentQuestions] = useState([]);
  const [reportQuestions, setReportQuestions] = useState([]);

  useEffect(() => {
    const fetchFeedback = async () => {
      try {
        setLoading(true);
        const res = await fetch(getApiUrl("/accounts/university/students/feedback/"), {
          method: "GET",
          credentials: "include",
        });
        if (!res.ok) throw new Error("Failed to fetch feedback");
        const data = await res.json();
        setAssessmentQuestions(mapApiToQuestions(data.assessmentFeedback, ASSESSMENT_QUESTIONS));
        setReportQuestions(mapApiToQuestions(data.reportFeedback, REPORT_QUESTIONS));
      } catch (e) {
        console.error("Feedback fetch error:", e);
        setError("Failed to load feedback data.");
      } finally {
        setLoading(false);
      }
    };
    fetchFeedback();
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3">
        <div className="w-8 h-8 rounded-full border-[3px] border-purple-500/20 border-t-purple-500 animate-spin" />
        <p className="text-text-sub text-sm">Loading feedback...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3">
        <p className="text-red-400 text-sm">{error}</p>
        <button onClick={() => window.location.reload()} className="px-4 py-2 rounded-lg bg-primary text-btn-text text-sm cursor-pointer">Retry</button>
      </div>
    );
  }

  return (
    <div className="p-8 h-full overflow-y-auto bg-screen-bg">
      <div className="max-w-5xl mx-auto flex flex-col gap-5">
        <div className="mb-1">
          <h1 className="text-2xl font-bold text-text-main mb-1">Students Feedback</h1>
          <p className="text-text-sub text-sm">View assessment and report ratings from students</p>
        </div>

        <SectionHeader title="Assessment Feedback" subtitle="Student responses on the BotGuru AI Prep-Interview assessment experience" gradient="linear-gradient(135deg, #9333ea, #c026d3)" />
        <div className="flex flex-col gap-4">
          {assessmentQuestions.map((q, i) => (
            <QuestionCard key={q.id} question={q} index={i} barColor="#bf34edad" />
          ))}
        </div>

        <SectionHeader title="Report Feedback" subtitle="Student responses on the evaluation report and performance analysis" gradient="linear-gradient(135deg, #9333ea, #c026d3)" />
        <div className="flex flex-col gap-4 pb-8">
          {reportQuestions.map((q, i) => (
            <QuestionCard key={q.id} question={q} index={i} barColor="#bf34edad" />
          ))}
        </div>
      </div>
    </div>
  );
}

export default StudentFeedback;